import React, { useState, useEffect } from "react";
import _ from "lodash";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TablePaginationUI from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";

import { Card } from "..";
import { TablePaginationProps } from "../../utils/types/components";

const useStyles = makeStyles({
  root: {
    width: "100%",
  },
  container: {
    maxHeight: 440,
  },
});

export const TablePaginationLegacy = ({
  rows,
  headers,
  getRowKey,
  style = {},
  rowsPerPageOptions = [10, 25, 50, 100],
  isLoading = false,
  vertical = false,
  container = "div",
}: any): React.ReactElement => {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    setPage(0);
  }, [rows]);

  const handleChangePage = (event: unknown, newPage: number) =>
    setPage(newPage);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const Container = ({
    children,
    ...props
  }: Record<string, unknown>): React.ReactElement => {
    if (container === "card") return <Card {...props}>{children}</Card>;
    return <div {...props}>{children}</div>;
  };

  if (isLoading)
    return (
      <Container
        className={classes.root}
        style={{
          minHeight: 200,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          ...style,
        }}>
        Cargando...
      </Container>
    );

  return (
    <Container className={classes.root} style={style}>
      <TableContainer className={classes.container}>
        <Table stickyHeader>
          {vertical || (
            <TableHead>
              <TableRow>
                {_.map(
                  headers,
                  ({ id, align, minWidth, style, label }, idx) => (
                    <TableCell
                      key={id || idx}
                      align={align}
                      style={style ? { minWidth, ...style } : { minWidth }}>
                      {label}
                    </TableCell>
                  ),
                )}
              </TableRow>
            </TableHead>
          )}

          <TableBody>
            {vertical &&
              _.map(
                headers,
                ({ id, align, minWidth, style, label, contentStyle }, idx) => {
                  const headerStyle = style
                    ? { minWidth, ...style }
                    : { minWidth };
                  return (
                    <TableRow key={id ?? idx} hover>
                      <TableCell
                        variant="head"
                        align={align}
                        style={headerStyle}>
                        {label}
                      </TableCell>

                      {_.map(
                        _.slice(
                          rows[idx],
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage,
                        ),
                        (row, i) => (
                          <TableCell
                            key={i}
                            style={contentStyle && contentStyle(row)}>
                            {row}
                          </TableCell>
                        ),
                      )}
                    </TableRow>
                  );
                },
              )}

            {vertical ||
              _.slice(
                rows,
                page * rowsPerPage,
                page * rowsPerPage + rowsPerPage,
              ).map((row, i) => {
                return (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={getRowKey ? getRowKey(row) : i}>
                    {_.map(
                      headers,
                      ({ id, align, format, contentStyle }, idx) => {
                        const value = id ? row[id] : row[idx];
                        return (
                          <TableCell
                            key={id || idx}
                            align={align}
                            style={contentStyle && contentStyle(row)}>
                            {format ? format(value) : value}
                          </TableCell>
                        );
                      },
                    )}
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      {vertical && (
        <TablePaginationUI
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={rows.reduce(
            (acc, arr) => (arr.length > acc ? arr.length : acc),
            0,
          )}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      )}

      {vertical || (
        <TablePaginationUI
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      )}
    </Container>
  );
};

export const TablePagination = ({
  data,
  headers,
  style = {},
  rowsPerPageOptions = [10, 25, 50, 100],
  isLoading = false,
  container = "div",
  getHeaderStyle = () => ({
    backgroundColor: "rgb(250,250,250)",
    fontWeight: "bold",
  }),
  getContentStyle = () => undefined,
}: TablePaginationProps): React.ReactElement => {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [collection, setCollection] = useState({ headers: [], rows: [] });

  useEffect(() => {
    setPage(0);
  }, [data, rowsPerPage]);

  useEffect(() => {
    const dataToShow = data
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      .reduce<Record<string, unknown[]>>((acc, item) => {
        const obj = { ...acc };

        headers.forEach(({ label, key }) => {
          const value = item[key];

          obj[label] = obj[label] ? [...obj[label], value] : [value];
        });

        return obj;
      }, {});

    setCollection({
      headers: Object.keys(dataToShow),
      rows: Object.values(dataToShow),
    });
  }, [data, page, rowsPerPage]);

  const handleChangePage = (event: unknown, newPage: number) =>
    setPage(newPage);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const Container = ({
    children,
    ...props
  }: Record<string, unknown>): React.ReactElement => {
    if (container === "card") return <Card {...props}>{children}</Card>;
    return <div {...props}>{children}</div>;
  };

  if (isLoading)
    return (
      <Container
        className={classes.root}
        style={{
          minHeight: 200,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          ...style,
        }}>
        Cargando...
      </Container>
    );

  return (
    <Container className={classes.root} style={style}>
      <TableContainer className={classes.container}>
        <Table stickyHeader>
          <TableBody>
            {collection.rows.map((item, i) => (
              <TableRow hover key={i}>
                <TableCell
                  key={collection.headers[i]}
                  style={getHeaderStyle(collection.headers[i])}>
                  {_.toString(collection.headers[i])}
                </TableCell>
                {_.map(item as unknown[], (value, idx) => (
                  <TableCell
                    key={idx}
                    style={getContentStyle(collection.headers[i], value)}>
                    {_.toString(value)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePaginationUI
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </Container>
  );
};
